import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import UpgradeModal, { LicenseDenialData } from '../components/licensing/UpgradeModal';
import { useLicenseUpdates, LicenseUpdateEvent } from '../hooks/useLicenseUpdates';
// HA-150: Upgrade prompt analytics
import {
  trackModalImpression,
  trackModalUpgradeClick,
  trackModalRemindLater,
  trackModalDismiss,
  trackToastImpression,
  trackToastUpgradeClick,
  trackApiDenial,
  AnalyticsUserContext,
} from '../services/upgradePromptAnalytics';
import { UserRole } from '../types/user';
import { normalizePracticeId } from '../utils/practiceIdentity';

import { useAuth } from './AuthContext';
import { useLicense } from './LicenseContext';

interface UpgradePromptState {
  isModalOpen: boolean;
  denialData: LicenseDenialData | null;
  isToastOpen: boolean;
  toastMessage: string;
  toastSeverity: 'success' | 'info' | 'warning' | 'error';
  toastShowUpgrade: boolean;
}

interface UpgradePromptContextValue {
  // Show the upgrade modal
  showUpgradeModal: (data?: LicenseDenialData) => void;

  // Show upgrade prompt with title and message (convenience wrapper)
  showUpgradePrompt: (title: string, message: string) => void;

  // Show a toast notification for quick feedback
  showUpgradeToast: (message: string) => void;

  // Handle 403 license denial from API
  handleLicenseDenial: (response: LicenseDenialResponse) => void;

  // HA-217: Handle generic 403 forbidden errors (not license denials)
  handleForbiddenError: (message?: string) => void;

  // Close modal/toast
  closeModal: () => void;
  closeToast: () => void;

  // Track if user dismissed (for "remind me later")
  dismissedFeatures: Set<string>;
  dismissFeature: (featureId: string) => void;

  // License update notifications (HA-111)
  showUpgradeSuccess: (oldTier: string, newTier: string) => void;
  showDowngradeNotice: (oldTier: string, newTier: string) => void;
  showLicenseRefreshNotice: () => void;
}

/**
 * API response shape for 403 license denials
 */
export interface LicenseDenialResponse {
  allowed: boolean;
  code: string;
  message: string;
  seats?: {
    entitled: number;
    used: number;
  };
  gracePeriodEndsUtc?: string;
}

/**
 * HA-476: what a client sees in place of any upgrade prompt. The plan is the practice's, so a client
 * has nothing to upgrade, and the denial's own message names tiers.
 */
export const CLIENT_FEATURE_UNAVAILABLE_MESSAGE =
  "This feature isn't available. Contact your practitioner.";

const UpgradePromptContext = createContext<UpgradePromptContextValue | undefined>(undefined);

interface UpgradePromptProviderProps {
  children: ReactNode;
  /**
   * Enable license update polling (default: true)
   */
  enablePolling?: boolean;
  /**
   * Polling interval in milliseconds (default: 60000 = 1 minute)
   */
  pollingInterval?: number;
}

/**
 * UpgradePromptProvider (HA-109, HA-111)
 * Global provider for handling upgrade prompts, 403 license denials,
 * and real-time license update notifications
 *
 * Usage:
 * - Wrap your app with <UpgradePromptProvider>
 * - Use useUpgradePrompt() hook to trigger prompts
 * - API interceptors can call handleLicenseDenial() on 403 responses
 * - License changes are automatically detected via polling (HA-111)
 */
export const UpgradePromptProvider: React.FC<UpgradePromptProviderProps> = ({
  children,
  enablePolling = true,
  pollingInterval = 60000,
}) => {
  const [state, setState] = useState<UpgradePromptState>({
    isModalOpen: false,
    denialData: null,
    isToastOpen: false,
    toastMessage: '',
    toastSeverity: 'warning',
    toastShowUpgrade: true,
  });

  const [dismissedFeatures, setDismissedFeatures] = useState<Set<string>>(new Set());

  // HA-150: Get user context for analytics
  const { currentUser } = useAuth();
  const license = useLicense();

  // HA-476: every upgrade prompt below goes through this. A user whose role isn't known yet gets
  // the neutral message too: until it is, they could be a client.
  const canManagePlan = !!currentUser?.role && currentUser.role !== UserRole.Client;

  const showFeatureUnavailable = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isModalOpen: false,
      isToastOpen: true,
      toastMessage: CLIENT_FEATURE_UNAVAILABLE_MESSAGE,
      toastSeverity: 'info',
      toastShowUpgrade: false,
    }));
  }, []);

  // HA-150: Build analytics context from user and license data
  const getAnalyticsContext = useCallback((): AnalyticsUserContext => {
    return {
      userId: currentUser?.id,
      // HA-357: normalize the composite practice id (`Name#owner-email`) to the
      // canonical practice name so the owner's email (PII) is never emitted to
      // analytics — neither the console logs nor the future backend `fetch`.
      // `userId` is the Cognito sub (not the email), so it stays as-is.
      practiceId: normalizePracticeId(currentUser?.practiceName),
      role: currentUser?.role,
      currentTier: license.tier ?? 'unknown',
    };
  }, [currentUser, license.tier]);

  const showUpgradeModal = useCallback(
    (data?: LicenseDenialData) => {
      if (!canManagePlan) {
        showFeatureUnavailable();

        return;
      }

      setState((prev) => ({
        ...prev,
        isModalOpen: true,
        denialData: data ?? null,
      }));

      // HA-150: Track modal impression
      trackModalImpression(
        getAnalyticsContext(),
        data?.featureName,
        data?.requiredTier?.toString(),
        data?.code
      );
    },
    [canManagePlan, getAnalyticsContext, showFeatureUnavailable]
  );

  const showUpgradePrompt = useCallback(
    (title: string, message: string) => {
      showUpgradeModal({ code: 'upgrade_prompt', featureName: title, message });
    },
    [showUpgradeModal]
  );

  const showUpgradeToast = useCallback(
    (message: string) => {
      if (!canManagePlan) {
        showFeatureUnavailable();

        return;
      }

      setState((prev) => ({
        ...prev,
        isToastOpen: true,
        toastMessage: message,
        toastSeverity: 'warning',
        toastShowUpgrade: true,
      }));

      // HA-150: Track toast impression
      trackToastImpression(getAnalyticsContext(), message);
    },
    [canManagePlan, getAnalyticsContext, showFeatureUnavailable]
  );

  // HA-111: License update notification methods
  const showUpgradeSuccess = useCallback((oldTier: string, newTier: string) => {
    setState((prev) => ({
      ...prev,
      isToastOpen: true,
      toastMessage: `Your plan has been upgraded to ${newTier}! New features are now available.`,
      toastSeverity: 'success',
      toastShowUpgrade: false,
    }));
  }, []);

  const showDowngradeNotice = useCallback((oldTier: string, newTier: string) => {
    setState((prev) => ({
      ...prev,
      isToastOpen: true,
      toastMessage: `Your plan has changed to ${newTier}. Some features may no longer be available.`,
      toastSeverity: 'info',
      toastShowUpgrade: true,
    }));
  }, []);

  const showLicenseRefreshNotice = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isToastOpen: true,
      toastMessage: 'Your license has been updated. Please refresh the page to see the changes.',
      toastSeverity: 'info',
      toastShowUpgrade: false,
    }));
  }, []);

  const handleLicenseDenial = useCallback(
    (response: LicenseDenialResponse) => {
      const denialData: LicenseDenialData = {
        code: response.code,
        message: response.message,
        seats: response.seats,
        gracePeriodEndsUtc: response.gracePeriodEndsUtc,
      };

      // Extract feature info from code if possible
      // Code format might be like "FEATURE_DENIED:telehealth" or "TIER_REQUIRED:Plus"
      if (response.code.includes(':')) {
        const [, value] = response.code.split(':');

        if (value) {
          denialData.featureName = value;
        }
      }

      // HA-150: Track API denial event
      trackApiDenial(getAnalyticsContext(), response.code, denialData.featureName, response.seats);

      // Check if this feature was recently dismissed
      if (denialData.featureName && dismissedFeatures.has(denialData.featureName)) {
        // Show toast instead of modal for dismissed features
        showUpgradeToast(response.message);

        return;
      }

      showUpgradeModal(denialData);
    },
    [dismissedFeatures, showUpgradeModal, showUpgradeToast, getAnalyticsContext]
  );

  // HA-217: Handle generic 403 forbidden errors (not license denials)
  const handleForbiddenError = useCallback((message?: string) => {
    setState((prev) => ({
      ...prev,
      isToastOpen: true,
      toastMessage: message || 'Access denied. You do not have permission to perform this action.',
      toastSeverity: 'error',
      toastShowUpgrade: false,
    }));
  }, []);

  const closeModal = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isModalOpen: false,
    }));
  }, []);

  const closeToast = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isToastOpen: false,
    }));
  }, []);

  const dismissFeature = useCallback((featureId: string) => {
    setDismissedFeatures((prev) => new Set([...prev, featureId]));
  }, []);

  const handleRemindLater = useCallback(() => {
    // HA-150: Track remind later click
    trackModalRemindLater(getAnalyticsContext(), state.denialData?.featureName);

    if (state.denialData?.featureName) {
      dismissFeature(state.denialData.featureName);
    }
    closeModal();
  }, [state.denialData, dismissFeature, closeModal, getAnalyticsContext]);

  // HA-150: Handle modal close with dismiss tracking
  const handleModalClose = useCallback(() => {
    trackModalDismiss(getAnalyticsContext(), state.denialData?.featureName);
    closeModal();
  }, [closeModal, getAnalyticsContext, state.denialData?.featureName]);

  // HA-150: Track upgrade button click from modal
  const handleModalUpgradeClick = useCallback(() => {
    trackModalUpgradeClick(
      getAnalyticsContext(),
      state.denialData?.featureName,
      state.denialData?.requiredTier?.toString()
    );
  }, [getAnalyticsContext, state.denialData]);

  // HA-150: Track upgrade button click from toast
  const handleToastUpgradeClick = useCallback(() => {
    trackToastUpgradeClick(getAnalyticsContext());
  }, [getAnalyticsContext]);

  // HA-111: License update polling integration
  const handleLicenseChange = useCallback(
    (event: LicenseUpdateEvent) => {
      switch (event.type) {
        case 'UPGRADE':
          showUpgradeSuccess(event.oldTier ?? 'previous plan', event.newTier);
          break;
        case 'DOWNGRADE':
          showDowngradeNotice(event.oldTier ?? 'previous plan', event.newTier);
          break;
        case 'REFRESH_REQUIRED':
        case 'FEATURE_CHANGE':
          showLicenseRefreshNotice();
          break;
      }
    },
    [showUpgradeSuccess, showDowngradeNotice, showLicenseRefreshNotice]
  );

  // Set up license update polling, for practice members only. /api/license/check
  // answers 403 for anyone without a practice: Clients aren't members in the
  // licensing sense, and a SystemAdmin belongs to no practice at all. Neither
  // has an upgrade flow to surface. An allow-list, so a future role defaults
  // to not polling rather than to a 403 a minute.
  //
  // HA-278: Require currentUser to be defined before enabling polling.
  // Otherwise the very first render fires the initial check before the role
  // is known — a silent 403 across every authenticated route.
  useLicenseUpdates({
    enabled:
      enablePolling &&
      !!currentUser &&
      (currentUser.role === UserRole.PracticeAdmin || currentUser.role === UserRole.Practitioner),
    pollingInterval,
    onLicenseChange: handleLicenseChange,
  });

  const value = useMemo<UpgradePromptContextValue>(
    () => ({
      showUpgradeModal,
      showUpgradePrompt,
      showUpgradeToast,
      handleLicenseDenial,
      handleForbiddenError,
      closeModal,
      closeToast,
      dismissedFeatures,
      dismissFeature,
      // HA-111: License update notifications
      showUpgradeSuccess,
      showDowngradeNotice,
      showLicenseRefreshNotice,
    }),
    [
      showUpgradeModal,
      showUpgradePrompt,
      showUpgradeToast,
      handleLicenseDenial,
      handleForbiddenError,
      closeModal,
      closeToast,
      dismissedFeatures,
      dismissFeature,
      showUpgradeSuccess,
      showDowngradeNotice,
      showLicenseRefreshNotice,
    ]
  );

  return (
    <UpgradePromptContext.Provider value={value}>
      {children}

      {/* Global Upgrade Modal */}
      <UpgradeModal
        open={canManagePlan && state.isModalOpen}
        onClose={handleModalClose}
        denialData={state.denialData ?? undefined}
        onRemindLater={handleRemindLater}
        onUpgradeClick={handleModalUpgradeClick}
      />

      {/* Global Toast/Snackbar */}
      <Snackbar
        open={state.isToastOpen}
        autoHideDuration={state.toastSeverity === 'success' ? 8000 : 6000}
        onClose={closeToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={closeToast}
          severity={state.toastSeverity}
          variant="filled"
          icon={
            state.toastSeverity === 'success' ? (
              <CheckCircleIcon fontSize="inherit" />
            ) : state.toastSeverity === 'info' ? (
              <InfoIcon fontSize="inherit" />
            ) : undefined
          }
          action={
            canManagePlan && state.toastShowUpgrade ? (
              <Button
                component={Link}
                to="/subscription"
                color="inherit"
                size="small"
                startIcon={<UpgradeIcon />}
                onClick={handleToastUpgradeClick}
              >
                {state.toastSeverity === 'success' ? 'View Plan' : 'Upgrade'}
              </Button>
            ) : undefined
          }
        >
          {state.toastMessage}
        </Alert>
      </Snackbar>
    </UpgradePromptContext.Provider>
  );
};

/**
 * Hook to access upgrade prompt functionality
 */
export const useUpgradePrompt = (): UpgradePromptContextValue => {
  const context = useContext(UpgradePromptContext);

  if (context === undefined) {
    throw new Error('useUpgradePrompt must be used within an UpgradePromptProvider');
  }

  return context;
};

/**
 * Utility function to check if an axios error is a license denial
 * Use this in API interceptors to trigger upgrade prompts
 */
export const isLicenseDenialError = (
  error: unknown
): error is { response: { status: number; data: LicenseDenialResponse } } => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const axiosError = error as { response?: { status?: number; data?: unknown } };

    if (
      axiosError.response?.status === 403 &&
      typeof axiosError.response.data === 'object' &&
      axiosError.response.data !== null &&
      'allowed' in axiosError.response.data &&
      (axiosError.response.data as { allowed: boolean }).allowed === false
    ) {
      return true;
    }
  }

  return false;
};

/**
 * HA-217: Utility function to check if an axios error is any 403 Forbidden error
 * Use this in API interceptors to handle all 403 errors gracefully
 */
export const isForbiddenError = (
  error: unknown
): error is { response: { status: number; data: unknown } } => {
  if (typeof error !== 'object' || error === null) {
    return false;
  }

  const err = error as {
    response?: { status?: number };
    status?: number;
    statusCode?: number;
  };

  // Check common error object structures (Axios, native Response, or custom)
  const status = err.response?.status ?? err.status ?? err.statusCode;

  return status === 403;
};

export default UpgradePromptContext;
